import { useLocation } from "react-router-dom";
import UserAvatar from "../../../components/ui/icons/UserAvatar";
import SectionTitle from "../../../components/ui/SectionTitle";
import { useCallback, useEffect, useRef, useState } from "react";
import useSurvey from "../../survey/hooks/useSurvey";
import { useProfile } from "../../auth/hooks/useProfile";
import { getActivityLogList } from "./api/activityLog.api";
import { ActivityLog as ActivityLogAttributes } from "./types/activityLog.types";
import ActivityLoader from "./components/ActivityLoader";

const LOCKER_ROOM_PATH = "/locker-room";

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const diffMs = date.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  const diffMonth = Math.round(diffDay / 30);
  const diffYear = Math.round(diffDay / 365);
  if (Math.abs(diffSec) < 60) return rtf.format(diffSec, "second");
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute");
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, "hour");
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, "day");
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, "month");
  return rtf.format(diffYear, "year");
}

const ActivityRow: React.FC<{ item: ActivityLogAttributes }> = ({ item }) => {
  const { user } = useProfile();
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#f0f3fa] last:border-0">
      <UserAvatar
        src={user?.image ?? ""}
        className="w-10 h-10 flex-shrink-0 mt-0.5"
      />

      <div className="min-w-0">
        <p
          className="text-[#26295e] font-semibold leading-snug mb-1"
          style={{ fontSize: 14, fontFamily: "var(--font-primary)" }}
        >
          {item.message}
        </p>
        <span
          className="text-[#9b9b9b]"
          style={{ fontSize: 13, fontFamily: "var(--font-primary)" }}
        >
          {getRelativeTime(item.createdAt)}
        </span>
      </div>
    </div>
  );
};

const ActivityLog = () => {
  const [activities, setActivities] = useState<ActivityLogAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);

  const location = useLocation();
  const { questionnaireId } = useSurvey();
  const { user } = useProfile();

  const fetchActivities = useCallback(
    async (pageNumber: number, append = false) => {
      if (!questionnaireId || !user?.id) return;

      append ? setLoadingMore(true) : setLoading(true);

      try {
        const res = await getActivityLogList({
          page: pageNumber,
          limit: 10,
          search: "",
          model: "",
          questionnaire_id: questionnaireId,
          user_id: user.id,
        });

        if (res.success) {
          const newRows = res.data.rows ?? [];

          setActivities((prev) => (append ? [...prev, ...newRows] : newRows));

          if (newRows.length < 10) setHasMore(false);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message ?? "Failed to fetch activities");
      } finally {
        append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [questionnaireId, user?.id],
  );

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;

    if (nearBottom) {
      setPage((prev) => {
        const nextPage = prev + 1;
        fetchActivities(nextPage, true);
        return nextPage;
      });
    }
  }, [loadingMore, hasMore, fetchActivities]);

  useEffect(() => {
    if (location.pathname !== LOCKER_ROOM_PATH) return;

    setPage(1);
    setHasMore(true);
    setError(null);
    fetchActivities(1, false);
  }, [location.pathname, questionnaireId, user?.id, fetchActivities]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [page, loadingMore, hasMore, handleScroll]);

  return (
    <div className="bg-white rounded-xl px-6 py-6 sm:px-8 sm:py-8 shadow-[0_2px_12px_rgba(0,0,0,0.06)] h-full flex flex-col">
      <SectionTitle>Activity</SectionTitle>

      {loading ? (
        <ActivityLoader />
      ) : error ? (
        <div className="flex items-center justify-center flex-1 py-10 font-medium text-red-500 text-[14px] font-primary">
          {error}
        </div>
      ) : activities.length === 0 ? (
        <div className="flex items-center justify-center flex-1 py-10 font-medium text-[#78909d] text-[18px] font-primary leading-5">
          No activity yet.
        </div>
      ) : (
        <div
          ref={listRef}
          className="overflow-y-auto -mx-1 px-1"
          style={{
            maxHeight: "350px",
            scrollbarWidth: "thin",
            scrollbarColor: "var(--light-blue) transparent",
          }}
        >
          {activities.map((item) => (
            <ActivityRow key={item._id} item={item} />
          ))}

          {loadingMore && (
            <div className="py-3 text-center text-sm text-gray-400">
              Loading more...
            </div>
          )}

          {!hasMore && (
            <div className="py-3 text-center text-xs text-gray-300">
              No more activity
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
